<template>
    <v-hover v-slot="{ hover }">
        <v-card
            v-bind="$attrs"
            :class="[
                'dashboard-view-card',
                { 'dashboard-view-card--dark': darkTheme }
            ]"
            :elevation="hover ? 8 : 2"
        >
            <div class="dashboard-view-card__media">
                <a :href="view.link" class="dashboard-view-card__media-link">
                    <v-img
                        :src="cardImage"
                        class="dashboard-view-card__image"
                        cover
                    />
                </a>
                <div
                    class="dashboard-view-card__chip"
                    :style="{ backgroundColor: viewTypeColor }"
                >
                    <v-icon
                        class="dashboard-view-card__chip-icon"
                        :style="darkTheme ? 'color: #ffffff !important;' : ''"
                    >
                        {{ viewTypeIcon }}
                    </v-icon>
                    <span class="dashboard-view-card__chip-text">
                        {{ viewTypeText }}
                    </span>
                </div>
            </div>
            <v-card-title
                class="dashboard-view-card__title"
                :style="
                    darkTheme
                        ? 'color: #ffffff !important;'
                        : 'color: #111111 !important;'
                "
            >
                <span
                    class="dashboard-view-card__title-text"
                    :style="
                        darkTheme
                            ? 'color: #ffffff !important;'
                            : 'color: #23242a !important;'
                    "
                >
                    {{ view.assetName }}
                </span>
            </v-card-title>
            <v-card-text class="dashboard-view-card__subtitle">
                <span v-line-clamp:20="2" class="dashboard-view-card__subtitle-text">
                    {{ view.siteName }}
                </span>
            </v-card-text>

            <div
                v-if="showInspectionAiSummary"
                class="dashboard-view-card__inspection-ai"
            >
                <div class="dashboard-view-card__inspection-ai-label">
                    Image AI
                </div>
                <div class="dashboard-view-card__inspection-ai-status">
                    {{ inspectionAiStatusLabel }}
                </div>
                <div class="dashboard-view-card__inspection-ai-meta">
                    {{ inspectionAiSummary.assetCount }} assets ·
                    {{ inspectionAiSummary.matchedCount }} matched ·
                    {{ inspectionAiSummary.imageOnlyCount }} image only
                </div>
            </div>

            <v-spacer class="mt-5 mb-5" />

            <v-card-actions class="dashboard-view-card__actions">
                <span
                    class="dashboard-view-card__date"
                    :style="darkTheme ? 'color: #ffffff !important;' : ''"
                >
                    {{ view.datasetDate }}
                </span>

                <v-btn
                    icon
                    class="dashboard-view-card__map-btn"
                    @click="redirectToMapView(view.location)"
                >
                    <v-icon :style="darkTheme ? 'color: #ffffff !important;' : ''">
                        {{ '$vuetify.icons.mapview' }}
                    </v-icon>
                </v-btn>

                <v-btn
                    text
                    class="dashboard-view-card__favourite-btn"
                    :loading="loading"
                    :disabled="disable"
                    @click="toggleFavourite(view.viewUuid)"
                >
                    <img
                        :src="
                            disable
                                ? require('img/svgs/star-gold.svg')
                                : require('img/svgs/star.svg')
                        "
                        alt="star"
                        width="24"
                    />
                    <span
                        class="dashboard-view-card__favourite-label"
                        :style="darkTheme ? 'color: #ffffff !important;' : ''"
                    >
                        {{ favouriteLabel }}
                    </span>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-hover>
</template>

<script>
import axios from 'axios';
import { trackDashboardEvents } from 'js/eventsTracking';
import viewsInfoMixin from 'js/mixins/viewsInfoMixin';
import { PRODUCTION_ENVIRONMENT } from 'js/utils/constant';
import { mapState } from 'pinia';
import { useGeneralStore } from 'store/pinia/useGeneralStore';

export default {
    name: 'RecentViewsCard',
    inheritAttrs: false,
    emits: ['add-to-favourites', 'remove-from-favourites'],
    mixins: [viewsInfoMixin],
    props: {
        disable: {
            type: Boolean,
            default: false
        },
        favourite: {
            type: Boolean,
            default: false
        },
        view: {
            type: Object,
            default: null
        },
        imageUrl: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            loading: false
        };
    },
    computed: {
        ...mapState(useGeneralStore, ['darkTheme']),
        cardImage() {
            return this.view.image ? this.view.image : this.imageUrl;
        },
        viewTypeText() {
            return this.view_options[this.view.viewType]?.text || 'View';
        },
        viewTypeIcon() {
            return this.view_options[this.view.viewType]?.icon || '$vuetify.icons.mapview';
        },
        viewTypeColor() {
            const colorByType = {
                Inspection: '#4B70F5',
                'Thermal Inspection': '#2A46AB',
                '3D Model': '#3CA086',
                '2D Map': '#6E4FC9',
                'Point Cloud': '#3F6DEB',
                'Terrain - DTM': '#7952C8',
                'Terrain - DSM': '#7952C8',
                'BIM Model': '#2B8EA1'
            };
            return colorByType[this.viewTypeText] || '#4B70F5';
        },
        inspectionAiSummary() {
            return this.view.inspectionAi || null;
        },
        showInspectionAiSummary() {
            return (
                !this.isProductionEnvironment &&
                Boolean(
                    this.inspectionAiSummary &&
                        (this.inspectionAiSummary.assetCount ||
                            this.inspectionAiSummary.observationCount ||
                            this.inspectionAiSummary.status)
                )
            );
        },
        isProductionEnvironment() {
            return window.ND_ENVIRONMENT === PRODUCTION_ENVIRONMENT;
        },
        inspectionAiStatusLabel() {
            const labels = {
                empty: 'Not Run',
                requested: 'Queued',
                processing: 'Running',
                complete: 'Complete',
                error: 'Error'
            };
            return labels[this.inspectionAiSummary?.status] || 'Not Run';
        },
        favouriteLabel() {
            if (this.disable) {
                return 'FAVOURITED';
            }
            return this.favourite ? 'REMOVE' : 'FAVOURITE';
        }
    },
    methods: {
        redirectToMapView(location) {
            window.location.href = `/mapview?location=${location}`;
        },
        toggleFavourite(viewUuid) {
            this.loading = true;
            if (this.favourite) {
                this.removeFromFavourites(viewUuid);
            } else {
                this.addToFavourites(viewUuid);
            }
        },
        addToFavourites(viewUuid) {
            trackDashboardEvents.favouriteAddedDashboard(this.view);
            axios
                .post('api/add_favourite_view/', {
                    viewUuid
                })
                .then(response => response.data)
                .then(() => {
                    this.$emit('add-to-favourites', viewUuid);
                    this.loading = false;
                })
                .catch(error => {
                    throw error;
                });
        },
        removeFromFavourites(viewUuid) {
            trackDashboardEvents.favouriteRemovedDashboard(this.view);
            axios
                .post(`api/remove_favourite_view/`, {
                    viewUuid
                })
                .then(response => response.data)
                .then(() => {
                    this.$emit('remove-from-favourites', viewUuid);
                    this.loading = false;
                })
                .catch(error => {
                    throw error;
                });
        }
    }
};
</script>

<style scoped>
.dashboard-view-card {
    display: flex;
    flex-direction: column;
    width: 272.25px;
    min-width: 272.25px;
    max-width: 272.25px;
    height: 370px;
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: rgba(0, 0, 0, 0.2) 0 3px 5px -1px,
        rgba(0, 0, 0, 0.14) 0 6px 10px 0,
        rgba(0, 0, 0, 0.12) 0 1px 18px 0 !important;
}

.dashboard-view-card__media {
    position: relative;
    height: 290px;
    overflow: hidden;
}

.dashboard-view-card__media-link {
    display: block;
    width: 100%;
    height: 100%;
}

.dashboard-view-card__image {
    width: 100%;
    height: 100%;
}

.dashboard-view-card__chip {
    position: absolute;
    right: 16px;
    bottom: 14px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 999px;
    color: #fff;
    font-weight: 600;
    font-size: 13px;
    line-height: 1;
    padding: 10px 16px;
}

.dashboard-view-card__chip-icon {
    color: #fff !important;
    font-size: 18px !important;
}

.dashboard-view-card__chip-text {
    white-space: nowrap;
}

.dashboard-view-card__title {
    padding: 18px 18px 4px 18px !important;
    color: #111111 !important;
}

.dashboard-view-card__title-text {
    display: block;
    font-size: 28px;
    line-height: 1.15;
    font-weight: 600;
    color: #23242a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.dashboard-view-card__subtitle {
    padding: 0 18px !important;
}

.dashboard-view-card__subtitle-text {
    display: block;
    font-size: 16px;
    line-height: 1.25;
    color: #6f7688;
    word-break: break-word;
}

.dashboard-view-card__inspection-ai {
    margin: 14px 18px 0;
    padding: 12px 14px;
    border-radius: 14px;
    background: rgba(75, 112, 245, 0.08);
}

.dashboard-view-card__inspection-ai-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #4b70f5;
}

.dashboard-view-card__inspection-ai-status {
    margin-top: 4px;
    font-size: 16px;
    font-weight: 700;
    color: #1f2942;
}

.dashboard-view-card__inspection-ai-meta {
    margin-top: 4px;
    font-size: 13px;
    color: #5f6b84;
}

.dashboard-view-card__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px !important;
}

.dashboard-view-card__date {
    font-size: 18px;
    color: #2a2e38;
    white-space: nowrap;
}

.dashboard-view-card__map-btn {
    min-width: 0 !important;
}

.dashboard-view-card__map-btn .v-icon {
    color: #7c7c7c !important;
    font-size: 34px !important;
}

.dashboard-view-card__favourite-btn {
    margin-left: auto;
    min-width: 0 !important;
    padding: 0 !important;
    letter-spacing: 0.08em !important;
}

.dashboard-view-card__favourite-label {
    margin-left: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #7d7d7d;
}

.dashboard-view-card--dark .dashboard-view-card__title,
.dashboard-view-card--dark .dashboard-view-card__title-text,
.dashboard-view-card--dark .dashboard-view-card__chip-icon,
.dashboard-view-card--dark .dashboard-view-card__map-btn .v-icon,
.dashboard-view-card--dark .dashboard-view-card__favourite-label {
    color: #ffffff !important;
}

.dashboard-view-card--dark .dashboard-view-card__subtitle-text {
    color: rgba(255, 255, 255, 0.88) !important;
}

.dashboard-view-card--dark .dashboard-view-card__inspection-ai {
    background: rgba(140, 179, 255, 0.14);
}

.dashboard-view-card--dark .dashboard-view-card__inspection-ai-status,
.dashboard-view-card--dark .dashboard-view-card__inspection-ai-meta {
    color: rgba(255, 255, 255, 0.9);
}

:deep(.v-theme--dark) .dashboard-view-card__title-text,
:deep(.theme--dark) .dashboard-view-card__title-text {
    color: #ffffff !important;
}

:deep(.v-theme--dark) .dashboard-view-card__title,
:deep(.theme--dark) .dashboard-view-card__title {
    color: #ffffff !important;
}

:deep(.v-theme--dark) .dashboard-view-card .dashboard-view-card__title-text,
:deep(.theme--dark) .dashboard-view-card .dashboard-view-card__title-text,
:deep(.v-theme--dark) .dashboard-view-card .dashboard-view-card__title,
:deep(.theme--dark) .dashboard-view-card .dashboard-view-card__title,
:deep(.v-theme--dark) .dashboard-view-card .dashboard-view-card__chip-icon,
:deep(.theme--dark) .dashboard-view-card .dashboard-view-card__chip-icon {
    color: #ffffff !important;
}

:deep(.v-theme--dark) .dashboard-view-card__subtitle-text,
:deep(.theme--dark) .dashboard-view-card__subtitle-text {
    color: rgba(255, 255, 255, 0.88) !important;
}

:deep(.v-theme--dark) .dashboard-view-card__map-btn .v-icon,
:deep(.theme--dark) .dashboard-view-card__map-btn .v-icon,
:deep(.v-theme--dark) .dashboard-view-card__favourite-label,
:deep(.theme--dark) .dashboard-view-card__favourite-label {
    color: #ffffff !important;
}

@media (max-width: 1600px) {
    .dashboard-view-card__title-text {
        font-size: 24px;
    }

    .dashboard-view-card__subtitle-text {
        font-size: 15px;
    }
}

@media (max-width: 960px) {
    .dashboard-view-card {
        min-width: 272.25px;
        max-width: 272.25px;
        height: 370px;
    }

    .dashboard-view-card__media {
        height: 240px;
    }

    .dashboard-view-card__title-text {
        font-size: 22px;
    }
}
</style>
